/**
 * @fileoverview TransactionModal Component
 *
 * Modal overlay for adding new transactions with type selection and form.
 * Handles income template allocation defaults and provides a complete
 * transaction creation workflow.
 *
 * @module components/TransactionModal/TransactionModal
 */
import { useState, useEffect } from 'react';
import TransactionTypeSelector from '../TransactionForm/TransactionTypeSelector';
import TransactionFormContainer from '../TransactionForm/TransactionFormContainer';
import TransactionModalErrors from './TransactionModalErrors';
import ButtonSpinner from '../Loading/ButtonSpinner';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './TransactionModal.module.css';

/**
 * TransactionModal Component
 *
 * Modal overlay for adding new transactions.
 * Displays transaction type selection and transaction form.
 *
 * Props:
 * - isOpen (boolean): Whether modal is visible
 * - onClose (function): Called when modal should close
 * - transactionHook (object): Transaction management hook
 * - budgetStartMonth (string): Budget start month for validation
 * - singleBankMode (boolean): Whether budget is in single bank mode
 */
function TransactionModal({ isOpen, onClose, transactionHook, budgetStartMonth, singleBankMode }) {
    const [isSaving, setIsSaving] = useState(false);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(isOpen);

    // Reset saving state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsSaving(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            {/* Dark Overlay */}
            <div className={styles.overlay}>
                {/* Modal Content */}
                <div className={styles.modal}>
                    {/* Close X Button */}
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className={styles.closeButton}
                        aria-label="Close"
                    >
                        ×
                    </button>

                    {/* Modal Header */}
                    <div className={styles.header}>
                        <h2 className={styles.title}>Add New Transaction</h2>
                    </div>

                    {/* Modal Body */}
                    <div className={styles.body}>
                        {/* Transaction Type Selection */}
                        {!transactionHook.transactionType ? (
                            <TransactionTypeSelector
                                onTypeSelect={(type) => {
                                    transactionHook.setTransactionType(type);
                                    // Default to using template for Income transactions
                                    if (type === 'Income') {
                                        transactionHook.setUseTemplateForIncome(true);
                                    } else {
                                        transactionHook.setUseTemplateForIncome(false);
                                    }
                                    // In single bank mode, auto-set paidDirectly to true for expenses
                                    if (singleBankMode && type === 'Expense') {
                                        transactionHook.setPaidDirectly(true);
                                    }
                                }}
                                onClose={onClose}
                            />
                        ) : (
                            // Transaction Form (when type is selected)
                            <TransactionFormContainer
                                transactionHook={transactionHook}
                                budgetStartMonth={budgetStartMonth}
                                onClose={onClose}
                                hideSubmitButton={true}
                                singleBankMode={singleBankMode}
                            />
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className={styles.footer}>
                        {/* Error Messages */}
                        {transactionHook.transactionType && (
                            <TransactionModalErrors errors={transactionHook.errors} />
                        )}

                        {/* Buttons */}
                        <div className={styles.buttonGroup}>
                            <button
                                onClick={onClose}
                                disabled={isSaving}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>

                            {/* Submit Button - only show when transaction type is selected */}
                            {transactionHook.transactionType && (
                                <button
                                    onClick={async () => {
                                        setIsSaving(true);
                                        try {
                                            const success = await transactionHook.addTransaction();
                                            if (success) {
                                                onClose();
                                                // Note: Don't set isSaving to false - modal will unmount
                                            } else {
                                                // Validation failed or API error - stay on modal
                                                setIsSaving(false);
                                            }
                                        } catch (error) {
                                            // Unexpected error
                                            setIsSaving(false);
                                        }
                                    }}
                                    disabled={isSaving}
                                    className={styles.submitButton}
                                >
                                    {isSaving && <ButtonSpinner />}
                                    {isSaving ? `Adding ${transactionHook.transactionType}...` : `Add ${transactionHook.transactionType}`}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default TransactionModal;
